import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { apiClient, WorkflowRequest } from '../api/client';
import { UserAvatar } from '../components/UserAvatar';
import { useAuth } from '../hooks/useAuth';
import {
  ReactFlow,
  Background,
  Controls,
  BackgroundVariant,
  MarkerType,
  type Node,
  type Edge,
  ReactFlowProvider,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import { TriggerNode } from '../components/workflows/nodes/TriggerNode';
import { ActionNode } from '../components/workflows/nodes/ActionNode';
import { ConditionNode } from '../components/workflows/nodes/ConditionNode';
import { ApprovalNode } from '../components/workflows/nodes/ApprovalNode';
import { LoopNode } from '../components/workflows/nodes/LoopNode';
import { ForEachNode } from '../components/workflows/nodes/ForEachNode';
import { WorkflowEdge } from '../components/workflows/WorkflowEdge';

import { Clock, CheckCircle, XCircle, MessageSquare, Loader2, Wand2, ChevronDown, ChevronUp, Edit, ChevronLeft, ChevronRight, Eye, X } from 'lucide-react';
import { formatInternalName } from '../utils/formatInternalName';

const nodeTypes = {
  trigger: TriggerNode,
  action: ActionNode,
  condition: ConditionNode,
  approval: ApprovalNode,
  refine_approval: ApprovalNode,
  loop: LoopNode,
  for_each: ForEachNode,
};

const edgeTypes = {
  workflow: WorkflowEdge,
};

const MARKER_END = { type: MarkerType.ArrowClosed as MarkerType, width: 16, height: 16 };

const STATUS_CONFIG: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline'; icon: React.ReactNode }> = {
  pending: { label: 'Pending', variant: 'secondary', icon: <Clock className="w-3 h-3" /> },
  awaiting_params: { label: 'Awaiting Details', variant: 'secondary', icon: <MessageSquare className="w-3 h-3" /> },
  awaiting_justification: { label: 'Awaiting Justification', variant: 'secondary', icon: <MessageSquare className="w-3 h-3" /> },
  awaiting_approval: { label: 'Awaiting Approval', variant: 'secondary', icon: <Clock className="w-3 h-3" /> },
  matched: { label: 'Matched', variant: 'default', icon: <CheckCircle className="w-3 h-3" /> },
  executing: { label: 'Executing', variant: 'default', icon: <Loader2 className="w-3 h-3 animate-spin" /> },
  completed: { label: 'Completed', variant: 'default', icon: <CheckCircle className="w-3 h-3" /> },
  failed: { label: 'Failed', variant: 'destructive', icon: <XCircle className="w-3 h-3" /> },
  no_workflow: { label: 'No Workflow', variant: 'destructive', icon: <XCircle className="w-3 h-3" /> },
  pending_confirmation: { label: 'Awaiting Confirmation', variant: 'secondary', icon: <MessageSquare className="w-3 h-3" /> },
  dismissed: { label: 'Dismissed', variant: 'outline', icon: <XCircle className="w-3 h-3" /> },
  approved: { label: 'Approved', variant: 'default', icon: <CheckCircle className="w-3 h-3" /> },
  rejected: { label: 'Rejected', variant: 'destructive', icon: <XCircle className="w-3 h-3" /> },
};

export default function WorkflowRequests() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [userMap, setUserMap] = useState<Record<string, { picture_url?: string }>>({});
  const { user, isLoading: authLoading } = useAuth();
  const isAdmin = user?.role === 'org_admin' || user?.role === 'ops_admin';
  const PER_PAGE = 15;

  // Admin: per-section state
  const [noWorkflowRequests, setNoWorkflowRequests] = useState<WorkflowRequest[]>([]);
  const [noWorkflowTotal, setNoWorkflowTotal] = useState(0);
  const [noWorkflowPage, setNoWorkflowPage] = useState(1);

  const [activeRequests, setActiveRequests] = useState<WorkflowRequest[]>([]);
  const [activeTotal, setActiveTotal] = useState(0);
  const [activePage, setActivePage] = useState(1);

  const [completedRequests, setCompletedRequests] = useState<WorkflowRequest[]>([]);
  const [completedTotal, setCompletedTotal] = useState(0);
  const [completedPage, setCompletedPage] = useState(1);
  const [completedCollapsed, setCompletedCollapsed] = useState(true);
  const [activeCollapsed, setActiveCollapsed] = useState(true);

  // Developer: single list state
  const [myRequests, setMyRequests] = useState<WorkflowRequest[]>([]);
  const [myTotal, setMyTotal] = useState(0);
  const [myPage, setMyPage] = useState(1);

  const loadRequests = useCallback(async () => {
    try {
      if (isAdmin) {
        const [noWf, active, completed] = await Promise.all([
          apiClient.getWorkflowRequests(noWorkflowPage, PER_PAGE, false, 'no_workflow'),
          apiClient.getWorkflowRequests(activePage, PER_PAGE, false, 'pending,awaiting_params,awaiting_justification,awaiting_approval,executing,matched'),
          apiClient.getWorkflowRequests(completedPage, PER_PAGE, false, 'approved,rejected,failed,completed'),
        ]);
        setNoWorkflowRequests(noWf.requests || []);
        setNoWorkflowTotal(noWf.total);
        setActiveRequests(active.requests || []);
        setActiveTotal(active.total);
        setCompletedRequests(completed.requests || []);
        setCompletedTotal(completed.total);
      } else {
        const data = await apiClient.getWorkflowRequests(myPage, 20, true);
        setMyRequests(data.requests || []);
        setMyTotal(data.total);
      }
    } catch (e: any) {
      setError(e.message || 'Failed to load requests');
    } finally {
      setLoading(false);
      setInitialLoadDone(true);
    }
  }, [isAdmin, noWorkflowPage, activePage, completedPage, myPage]);

  useEffect(() => {
    apiClient.listTenantUsers().then((data: any) => {
      const users = data.users || data || [];
      const map: Record<string, { picture_url?: string }> = {};
      for (const u of users) {
        if (u.email) map[u.email] = { picture_url: u.picture_url };
      }
      setUserMap(map);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (authLoading) return;
    loadRequests();
    const interval = setInterval(loadRequests, 15000);
    return () => clearInterval(interval);
  }, [loadRequests, authLoading]);

  if (loading || authLoading || !initialLoadDone) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-140px)]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-8">
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {!isAdmin ? (
        /* Developer view: unified table of all their requests with status */
        <div className="space-y-3">
          <h2 className="text-sm font-semibold">Your Requests ({myTotal})</h2>
          {myRequests.length === 0 ? (
            <p className="text-sm text-muted-foreground">You haven't made any workflow requests yet.</p>
          ) : (
            <>
            <div className="rounded-lg border overflow-x-auto md:overflow-visible">
              <table className="w-full text-sm min-w-[720px] md:min-w-0">
                <thead>
                  <tr className="border-b">
                    <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Request</th>
                    <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Status</th>
                    <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Category</th>
                    <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Source</th>
                    <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">From</th>
                    <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Requested</th>
                    <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Parameters</th>
                  </tr>
                </thead>
                <tbody>
                  {myRequests.map(req => (
                    <RequestTableRow key={req.id} request={req} onRefresh={loadRequests} userMap={userMap} />
                  ))}
                </tbody>
              </table>
            </div>
            {myTotal > 20 && (() => {
              const totalPages = Math.ceil(myTotal / 20);
              return (
                <div className="flex items-center justify-between pt-2">
                  <span className="text-sm text-muted-foreground">Showing {(myPage - 1) * 20 + 1}–{Math.min(myPage * 20, myTotal)} of {myTotal}</span>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => setMyPage(p => Math.max(1, p - 1))} disabled={myPage === 1}>Previous</Button>
                    <span className="text-sm text-muted-foreground">Page {myPage} of {totalPages}</span>
                    <Button variant="outline" size="sm" onClick={() => setMyPage(p => Math.min(totalPages, p + 1))} disabled={myPage >= totalPages}>Next</Button>
                  </div>
                </div>
              );
            })()}
            </>
          )}
        </div>
      ) : (
        /* Admin view: segmented tables (pending review, active, completed) */
        <>
      {noWorkflowTotal > 0 && (
        <div className="space-y-3">
          <h2 className="text-sm font-semibold">Requests ({noWorkflowTotal})</h2>
          <div className="rounded-lg border overflow-x-auto md:overflow-visible">
            <table className="w-full text-sm min-w-[720px] md:min-w-0">
              <thead>
                <tr className="border-b">
                  <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Request</th>
                  <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Category</th>
                  <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Source</th>
                  <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">From</th>
                  <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Requested</th>
                  <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Parameters</th>
                  <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Workflow</th>
                  <th className="text-right px-4 py-2.5 font-medium text-muted-foreground"></th>
                </tr>
              </thead>
              <tbody>
                {noWorkflowRequests.map(req => (
                  <RequestTableRow key={req.id} request={req} showActions onRefresh={loadRequests} userMap={userMap} />
                ))}
              </tbody>
            </table>
          </div>
          {noWorkflowTotal > PER_PAGE && (() => {
            const totalPages = Math.ceil(noWorkflowTotal / PER_PAGE);
            return (
              <div className="flex items-center justify-between pt-2">
                <span className="text-sm text-muted-foreground">Showing {(noWorkflowPage - 1) * PER_PAGE + 1}–{Math.min(noWorkflowPage * PER_PAGE, noWorkflowTotal)} of {noWorkflowTotal}</span>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setNoWorkflowPage(p => Math.max(1, p - 1))} disabled={noWorkflowPage === 1}>Previous</Button>
                  <span className="text-sm text-muted-foreground">Page {noWorkflowPage} of {totalPages}</span>
                  <Button variant="outline" size="sm" onClick={() => setNoWorkflowPage(p => Math.min(totalPages, p + 1))} disabled={noWorkflowPage >= totalPages}>Next</Button>
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {activeTotal > 0 && (
        <div className="space-y-3">
          <button
            onClick={() => setActiveCollapsed(!activeCollapsed)}
            className="flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors"
          >
            {activeCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            Active Requests ({activeTotal})
          </button>
          {!activeCollapsed && (
            <>
          <div className="rounded-lg border overflow-x-auto md:overflow-visible">
            <table className="w-full text-sm min-w-[720px] md:min-w-0">
              <thead>
                <tr className="border-b">
                  <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Request</th>
                  <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Status</th>
                  <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Category</th>
                  <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Source</th>
                  <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">From</th>
                  <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Requested</th>
                  <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Parameters</th>
                </tr>
              </thead>
              <tbody>
                {activeRequests.map(req => (
                  <RequestTableRow key={req.id} request={req} onRefresh={loadRequests} userMap={userMap} />
                ))}
              </tbody>
            </table>
          </div>
          {activeTotal > PER_PAGE && (() => {
            const totalPages = Math.ceil(activeTotal / PER_PAGE);
            return (
              <div className="flex items-center justify-between pt-2">
                <span className="text-sm text-muted-foreground">Showing {(activePage - 1) * PER_PAGE + 1}–{Math.min(activePage * PER_PAGE, activeTotal)} of {activeTotal}</span>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setActivePage(p => Math.max(1, p - 1))} disabled={activePage === 1}>Previous</Button>
                  <span className="text-sm text-muted-foreground">Page {activePage} of {totalPages}</span>
                  <Button variant="outline" size="sm" onClick={() => setActivePage(p => Math.min(totalPages, p + 1))} disabled={activePage >= totalPages}>Next</Button>
                </div>
              </div>
            );
          })()}
            </>
          )}
        </div>
      )}

      {(completedTotal > 0 || completedRequests.length > 0) && (
        <div className="space-y-3">
          <button
            onClick={() => setCompletedCollapsed(!completedCollapsed)}
            className="flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors"
          >
            {completedCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            Completed ({completedTotal})
          </button>
          {!completedCollapsed && (
            <>
          <div className="rounded-lg border overflow-x-auto md:overflow-visible">
            <table className="w-full text-sm min-w-[720px] md:min-w-0">
              <thead>
                <tr className="border-b">
                  <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Request</th>
                  <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Status</th>
                  <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Category</th>
                  <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Source</th>
                  <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">From</th>
                  <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Requested</th>
                  <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Parameters</th>
                </tr>
              </thead>
              <tbody>
                {completedRequests.map(req => (
                  <RequestTableRow key={req.id} request={req} onRefresh={loadRequests} userMap={userMap} />
                ))}
              </tbody>
            </table>
          </div>
          {completedTotal > PER_PAGE && (() => {
            const totalPages = Math.ceil(completedTotal / PER_PAGE);
            return (
              <div className="flex items-center justify-between pt-2">
                <span className="text-sm text-muted-foreground">Showing {(completedPage - 1) * PER_PAGE + 1}–{Math.min(completedPage * PER_PAGE, completedTotal)} of {completedTotal}</span>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setCompletedPage(p => Math.max(1, p - 1))} disabled={completedPage === 1}>Previous</Button>
                  <span className="text-sm text-muted-foreground">Page {completedPage} of {totalPages}</span>
                  <Button variant="outline" size="sm" onClick={() => setCompletedPage(p => Math.min(totalPages, p + 1))} disabled={completedPage >= totalPages}>Next</Button>
                </div>
              </div>
            );
          })()}
            </>
          )}
        </div>
      )}

      {initialLoadDone && noWorkflowTotal === 0 && activeTotal === 0 && completedTotal === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <h3 className="text-lg font-medium">No requests yet</h3>
            <p className="text-muted-foreground mt-1 max-w-lg mx-auto">
              When team members make requests via Slack or the CLI, their requests will appear here.
            </p>
          </CardContent>
        </Card>
      )}
        </>
      )}
    </div>
  );
}

function ExpandableCell({ text, maxWidth = 'max-w-[250px]' }: { text: string; maxWidth?: string }) {
  const [expanded, setExpanded] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const textRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const el = textRef.current;
    if (el) setIsTruncated(el.scrollWidth > el.clientWidth);
  }, [text]);

  if (!text) return <span className="text-muted-foreground">—</span>;
  return (
    <div className={`${maxWidth} relative`}>
      <div className="flex items-center gap-1">
        <span ref={textRef} className="truncate">{text}</span>
        {isTruncated && (
          <button onClick={() => setExpanded(!expanded)} className="shrink-0 text-muted-foreground hover:text-foreground">
            {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
        )}
      </div>
      {expanded && isTruncated && (
        <div className="absolute z-50 left-0 top-full mt-1 p-2 rounded-md border bg-popover text-popover-foreground shadow-md text-xs max-w-[400px] whitespace-normal break-words">
          {text}
        </div>
      )}
    </div>
  );
}

function WorkflowPreviewDialog({ definition, onClose }: { definition: any; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-background border rounded-lg shadow-lg w-[80vw] h-[60vh] relative" onClick={e => e.stopPropagation()}>
        <Button variant="ghost" size="sm" className="absolute top-2 right-2 z-10 h-8 w-8 p-0" onClick={onClose}>
          <X className="w-4 h-4" />
        </Button>
        <div className="w-full h-full p-2">
          <SuggestedWorkflowPreview definition={definition} fullSize />
        </div>
      </div>
    </div>
  );
}

function RequestTableRow({ request, showActions, onRefresh, userMap }: { request: WorkflowRequest; showActions?: boolean; onRefresh: () => void; userMap?: Record<string, { picture_url?: string }> }) {
  const navigate = useNavigate();
  const [rejectLoading, setRejectLoading] = useState(false);
  const [showWorkflow, setShowWorkflow] = useState(false);
  const params = typeof request.parameters === 'string' ? JSON.parse(request.parameters) : request.parameters;
  const paramEntries = Object.entries(params || {}).filter(([, v]) => v !== null && v !== '');
  const createdAt = new Date(request.created_at).toLocaleString('en-US', { timeZone: 'UTC', dateStyle: 'medium', timeStyle: 'short' }) + ' UTC';
  const hasSuggested = request.suggested_workflow && request.suggested_workflow.name;
  const source = request.source === 'slack' ? 'Slack' : request.source === 'cli' ? 'CLI' : request.source === 'platform' ? 'Platform' : request.source || '—';
  const effectiveEmail = request.requester_email || (request.requester_name?.includes('@') ? request.requester_name : '');

  const handleCreate = () => {
    const sw = request.suggested_workflow;
    navigate('/workflows/new', {
      state: {
        suggestion: {
          name: sw?.name || request.prompt,
          description: sw?.description || request.prompt,
          definition: sw?.definition,
          trigger_config: sw?.trigger_config,
          nl_prompt: sw?.nl_prompt || request.prompt,
        },
        fromRequest: request.id,
      },
    });
  };

  const handleReject = async () => {
    setRejectLoading(true);
    try { await apiClient.workflowRequestAction(request.id, 'reject'); onRefresh(); } catch { /* handled */ } finally { setRejectLoading(false); }
  };

  const paramSummary = paramEntries.map(([k, v]) => `${k}: ${String(v)}`).join(', ');

  const statusInfo = STATUS_CONFIG[request.status] || STATUS_CONFIG.pending;

  return (
    <>
    <tr className="border-b last:border-0 hover:bg-muted/20 transition-colors">
      <td className="px-4 py-2.5">
        <ExpandableCell text={request.prompt} maxWidth="max-w-[280px]" />
      </td>
      {!showActions && (
        <td className="px-4 py-2.5">
          <Badge
            variant="outline"
            className={`text-xs font-medium ${
              request.status === 'approved' || request.status === 'completed' || request.status === 'matched' || request.status === 'executing'
                ? 'text-green-600 dark:text-green-400 border-green-300 dark:border-green-800'
                : request.status === 'rejected' || request.status === 'failed' || request.status === 'no_workflow'
                  ? 'text-red-500 dark:text-red-400 border-red-300 dark:border-red-800'
                  : 'text-yellow-600 dark:text-yellow-400 border-yellow-300 dark:border-yellow-700'
            }`}
          >
            {statusInfo.label}
          </Badge>
        </td>
      )}
      <td className="px-4 py-2.5">
        <Badge variant="outline" className="text-xs">{request.category ? formatInternalName(request.category) : 'Uncategorized'}</Badge>
      </td>
      <td className="px-4 py-2.5 text-muted-foreground text-xs">{source}</td>
      <td className="px-4 py-2.5 text-muted-foreground text-xs">
        {effectiveEmail ? (
          <div className="flex items-center gap-1.5">
            <UserAvatar email={effectiveEmail} pictureUrl={userMap?.[effectiveEmail]?.picture_url} />
            <span className="truncate">{request.requester_name || effectiveEmail}</span>
          </div>
        ) : (request.requester_name || '—')}
      </td>
      <td className="px-4 py-2.5 text-muted-foreground text-xs whitespace-nowrap">{createdAt}</td>
      <td className="px-4 py-2.5 text-xs">
        <ExpandableCell text={paramSummary} maxWidth="max-w-[200px]" />
      </td>
      {showActions && (
        <>
        <td className="px-4 py-2.5 text-xs">
          {hasSuggested ? (
            <button onClick={() => setShowWorkflow(true)} className="text-primary hover:underline flex items-center gap-1">
              <Eye className="w-3 h-3" /> View Workflow
            </button>
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </td>
        <td className="px-4 py-2.5 text-right">
          <div className="flex items-center gap-2 justify-end">
            <Button size="sm" onClick={handleCreate} className="gap-1 h-7 text-xs">
              <Edit className="w-3 h-3" />
              {hasSuggested ? 'Review & Deploy' : 'Create Workflow'}
            </Button>
            <Button variant="outline" size="sm" onClick={handleReject} disabled={rejectLoading} className="h-7 text-xs">
              Reject
            </Button>
          </div>
        </td>
        </>
      )}
    </tr>
    {showWorkflow && hasSuggested && (
      <WorkflowPreviewDialog definition={request.suggested_workflow!.definition} onClose={() => setShowWorkflow(false)} />
    )}
    </>
  );
}

function SuggestedWorkflowPreview({ definition, fullSize }: { definition: any; fullSize?: boolean }) {
  const { nodes: flowNodes, edges: flowEdges } = useMemo(() => {
    if (!definition?.nodes || !definition?.edges) return { nodes: [], edges: [] };
    const nodes: Node[] = definition.nodes.map((n: any) => ({
      id: n.id,
      type: n.type,
      data: n.data,
      position: n.position || { x: 0, y: 0 },
    }));
    const edges: Edge[] = definition.edges.map((e: any, i: number) => ({
      id: e.id || `auto-e${i + 1}`,
      source: e.source,
      target: e.target,
      label: e.label,
      type: 'workflow',
      markerEnd: MARKER_END,
      animated: true,
    }));
    return { nodes, edges };
  }, [definition]);

  if (flowNodes.length === 0) return null;

  return (
    <div className={`${fullSize ? 'h-full' : 'h-[200px]'} w-full rounded border bg-muted/20`}>
      <ReactFlowProvider>
        <ReactFlow
          nodes={flowNodes}
          edges={flowEdges}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          fitView
          fitViewOptions={{ padding: 0.3, minZoom: 0.3, maxZoom: 1.1 }}
          proOptions={{ hideAttribution: true }}
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable={false}
          panOnDrag={false}
          zoomOnScroll={false}
          zoomOnPinch={false}
          zoomOnDoubleClick={false}
          preventScrolling={false}
        >
          <Background variant={BackgroundVariant.Dots} gap={20} size={1} />
        </ReactFlow>
      </ReactFlowProvider>
    </div>
  );
}

function RequestCard({
  request,
  showActions,
  onRefresh,
  userMap,
}: {
  request: WorkflowRequest;
  showActions?: boolean;
  onRefresh: () => void;
  userMap?: Record<string, { picture_url?: string }>;
}) {
  const navigate = useNavigate();
  const [showSuggested, setShowSuggested] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [rejectLoading, setRejectLoading] = useState(false);

  const statusInfo = STATUS_CONFIG[request.status] || STATUS_CONFIG.pending;
  const params = typeof request.parameters === 'string' ? JSON.parse(request.parameters) : request.parameters;
  const paramEntries = Object.entries(params || {}).filter(([, v]) => v !== null && v !== '');
  const createdAt = new Date(request.created_at).toLocaleString('en-US', { timeZone: 'UTC', dateStyle: 'medium', timeStyle: 'short' }) + ' UTC';
  const hasSuggested = request.suggested_workflow && request.suggested_workflow.name;
  const effectiveEmail = request.requester_email || (request.requester_name?.includes('@') ? request.requester_name : '');

  const handleCreateWorkflow = () => {
    const sw = request.suggested_workflow;
    navigate('/workflows/new', {
      state: {
        suggestion: {
          name: sw?.name || request.prompt,
          description: sw?.description || request.prompt,
          definition: sw?.definition,
          trigger_config: sw?.trigger_config,
          nl_prompt: sw?.nl_prompt || request.prompt,
        },
        fromRequest: request.id,
      },
    });
  };

  const handleReject = async () => {
    setRejectLoading(true);
    try {
      await apiClient.workflowRequestAction(request.id, 'reject');
      onRefresh();
    } catch {
      // handled by parent
    } finally {
      setRejectLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <CardTitle className="text-base font-medium truncate">{request.prompt}</CardTitle>
            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
              <span className={`text-xs font-semibold ${
                request.status === 'approved' || request.status === 'completed' || request.status === 'matched'
                  ? 'text-green-600 dark:text-green-400'
                  : request.status === 'rejected' || request.status === 'failed' || request.status === 'no_workflow'
                    ? 'text-red-600 dark:text-red-400'
                    : 'text-muted-foreground'
              }`}>{statusInfo.label}</span>
              {request.category && (
                <Badge variant="outline" className="text-xs">{formatInternalName(request.category)}</Badge>
              )}
              <span className="text-xs text-muted-foreground">
                {request.source === 'slack' ? 'via Slack' : request.source} · {createdAt}
              </span>
              {request.requester_name && (
                <span className="text-xs text-muted-foreground flex items-center gap-1">
                  by {effectiveEmail && <UserAvatar email={effectiveEmail} pictureUrl={userMap?.[effectiveEmail]?.picture_url} />}
                  {request.requester_name}
                </span>
              )}
            </div>
          </div>
          {showActions && (
            <div className="flex items-center gap-2 shrink-0">
              <Button
                size="sm"
                variant="outline"
                onClick={handleReject}
                disabled={rejectLoading}
              >
                {rejectLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                Reject
              </Button>
              <Button
                size="sm"
                onClick={handleCreateWorkflow}
                disabled={actionLoading}
                className="gap-1"
              >
                <Edit className="w-4 h-4" />
                {hasSuggested ? 'Review & Deploy' : 'Create Workflow'}
              </Button>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="pt-0 space-y-3">
        {paramEntries.length > 0 && (
          <div className="text-xs space-y-1">
            <p className="font-medium text-muted-foreground">Collected Parameters:</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1">
              {paramEntries.map(([key, value]) => (
                <div key={key} className="flex gap-1">
                  <span className="text-muted-foreground">{key}:</span>
                  <span className="font-medium truncate">{String(value)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        {request.execution_error && (
          <p className="text-xs text-destructive">{request.execution_error}</p>
        )}
        {hasSuggested && (
          <div className="border rounded-md">
            <button
              className="w-full flex items-center justify-between p-3 text-sm hover:bg-accent/50 transition-colors"
              onClick={() => setShowSuggested(!showSuggested)}
            >
              <div className="flex items-center gap-2">
                <Wand2 className="w-4 h-4 text-primary" />
                <span className="font-medium">AI-Suggested Workflow: {request.suggested_workflow!.name}</span>
              </div>
              {showSuggested ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            {showSuggested && (
              <div className="p-3 pt-0 space-y-2">
                <p className="text-xs text-muted-foreground">{request.suggested_workflow!.description}</p>
                {request.suggested_workflow!.explanation && (
                  <p className="text-xs text-muted-foreground italic">{request.suggested_workflow!.explanation}</p>
                )}
                <SuggestedWorkflowPreview definition={request.suggested_workflow!.definition} />
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
